import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { getOpsDashboard } from "@/lib/admin/operations.functions";
import { toneClass, type OpsTone } from "@/lib/admin/order-badges";

export const Route = createFileRoute("/_admin/admin/operations")({
  component: OperationsDashboardPage,
});

const REFRESH_MS = 30_000;

function istToday(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60_000);
  return ist.toISOString().slice(0, 10);
}

function inr(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function Card({
  label,
  value,
  tone,
  loading,
}: {
  label: string;
  value: string | number;
  tone: OpsTone;
  loading: boolean;
}) {
  return (
    <div className={`rounded-lg border p-3 ${toneClass(tone)}`}>
      <p className="truncate text-[11px] font-medium uppercase tracking-wide opacity-80">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-6 w-12" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      )}
    </div>
  );
}

function OperationsDashboardPage() {
  const [day, setDay] = useState(istToday());
  const fetchFn = useServerFn(getOpsDashboard);

  const q = useQuery({
    queryKey: ["admin", "ops-dashboard", day],
    queryFn: () => fetchFn({ data: { day } }),
    refetchInterval: REFRESH_MS,
    staleTime: 10_000,
  });

  const loading = q.isLoading;
  const channel = (k: string) => q.data?.by_channel?.[k] ?? 0;
  const status = (k: string) => q.data?.by_status?.[k] ?? 0;

  return (
    <div>
      <AdminPageHeader
        title="Operations Dashboard"
        description="Live counters for the selected service day (IST). Refreshes every 30 seconds."
      />

      <div className="mb-4 flex items-center gap-2">
        <label htmlFor="ops-day" className="text-sm text-muted-foreground">
          Day
        </label>
        <Input
          id="ops-day"
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value || istToday())}
          className="h-9 w-44"
        />
        {q.isFetching ? <span className="text-xs text-muted-foreground">Refreshing…</span> : null}
      </div>

      <section aria-label="Order volume" className="mb-6">
        <h2 className="mb-2 text-sm font-medium text-foreground">Order volume</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-5">
          <Card label="Today's orders" value={q.data?.total ?? 0} tone="info" loading={loading} />
          <Card
            label="Revenue"
            value={inr(q.data?.revenue_paise ?? 0)}
            tone="success"
            loading={loading}
          />
          <Card label="Delivered" value={status("delivered")} tone="success" loading={loading} />
          <Card
            label="Cancelled"
            value={status("cancelled") + status("failed")}
            tone="danger"
            loading={loading}
          />
          <Card label="Refunded" value={status("refunded")} tone="neutral" loading={loading} />
        </div>
      </section>

      <section aria-label="Orders by source" className="mb-6">
        <h2 className="mb-2 text-sm font-medium text-foreground">Orders by source</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <Card label="Website" value={channel("website")} tone="info" loading={loading} />
          <Card label="Phone" value={channel("phone")} tone="teal" loading={loading} />
          <Card label="Walk-in" value={channel("walkin")} tone="warn" loading={loading} />
          <Card label="WhatsApp" value={channel("whatsapp")} tone="success" loading={loading} />
          <Card label="Corporate" value={channel("corporate")} tone="purple" loading={loading} />
          <Card
            label="Subscription"
            value={channel("subscription")}
            tone="neutral"
            loading={loading}
          />
        </div>
      </section>

      <section aria-label="Kitchen pipeline">
        <h2 className="mb-2 text-sm font-medium text-foreground">Kitchen pipeline</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Card
            label="Pending"
            value={status("new") + status("confirmed") + status("accepted")}
            tone="neutral"
            loading={loading}
          />
          <Card label="Preparing" value={status("preparing")} tone="warn" loading={loading} />
          <Card label="Ready" value={status("ready")} tone="teal" loading={loading} />
          <Card
            label="Out for delivery"
            value={status("out_for_delivery")}
            tone="purple"
            loading={loading}
          />
        </div>
      </section>
    </div>
  );
}
